import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import SearchableCombobox from '@/components/ui/searchable-combobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable TipoGanado + Categoria selector with searchable combos and quick-add buttons.
 *
 * Props:
 *   tipoGanado       - current tipoGanado name (string)
 *   categoria      - current categoria name (string)
 *   onTipoGanadoChange(nombre)  - callback when tipoGanado changes
 *   onCategoriaChange(nombre) - callback when categoria changes
 *   errors      - { tipoGanado, categoria } error messages
 *   disabled    - boolean
 */
const TipoGanadoCategoriaSelector = ({ tipoGanado, categoria, onTipoGanadoChange, onCategoriaChange, errors = {}, disabled = false }) => {
  const [tipoGanados, setTipoGanados] = useState([]);
  const [categorias, setCategorias] = useState([]);

  // Quick-add modals
  const [showAddTipoGanado, setShowAddTipoGanado] = useState(false);
  const [showAddCategoria, setShowAddCategoria] = useState(false);
  const [newTipoGanadoNombre, setNewTipoGanadoNombre] = useState('');
  const [newCategoriaNombre, setNewCategoriaNombre] = useState('');
  const [addingTipoGanado, setAddingTipoGanado] = useState(false);
  const [addingCategoria, setAddingCategoria] = useState(false);

  // Track the selected tipoGanado object to get its ID for loading models
  const [selectedTipoGanadoObj, setSelectedTipoGanadoObj] = useState(null);

  // Load tipoGanados on mount
  useEffect(() => {
    loadTipoGanados();
  }, []);

  // When tipoGanado prop changes, find its object and load models
  useEffect(() => {
    if (tipoGanado && tipoGanados.length > 0) {
      const found = tipoGanados.find(m => m.nombre.toLowerCase() === tipoGanado.toLowerCase());
      if (found) {
        setSelectedTipoGanadoObj(found);
        loadCategorias(found.id);
      } else {
        setSelectedTipoGanadoObj(null);
        setCategorias([]);
      }
    } else {
      setSelectedTipoGanadoObj(null);
      setCategorias([]);
    }
  }, [tipoGanado, tipoGanados]);

  const loadTipoGanados = async () => {
    try {
      const response = await axios.get(`${API}/tipoGanados`);
      setTipoGanados(response.data);
    } catch (error) {
      console.error('Error loading tipoGanados:', error);
    }
  };

  const loadCategorias = async (tipoGanadoId) => {
    try {
      const response = await axios.get(`${API}/tipoGanados/${tipoGanadoId}/categorias`);
      setCategorias(response.data);
    } catch (error) {
      console.error('Error loading categorias:', error);
    }
  };

  const handleTipoGanadoSelect = (tipoGanadoNombre) => {
    onTipoGanadoChange(tipoGanadoNombre);
    // Clear categoria when tipoGanado changes
    if (tipoGanadoNombre.toLowerCase() !== tipoGanado?.toLowerCase()) {
      onCategoriaChange('');
    }
  };

  const handleCategoriaSelect = (categoriaNombre) => {
    onCategoriaChange(categoriaNombre);
  };

  const handleAddTipoGanado = async () => {
    if (!newTipoGanadoNombre.trim()) {
      toast.error('El nombre de la tipoGanado es requerido');
      return;
    }
    setAddingTipoGanado(true);
    try {
      const response = await axios.post(`${API}/tipoGanados`, { nombre: newTipoGanadoNombre.trim() });
      toast.success(`TipoGanado "${response.data.nombre}" creada`);
      setShowAddTipoGanado(false);
      setNewTipoGanadoNombre('');
      await loadTipoGanados();
      // Auto-select the new tipoGanado
      onTipoGanadoChange(response.data.nombre);
      onCategoriaChange('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear tipoGanado');
    } finally {
      setAddingTipoGanado(false);
    }
  };

  const handleAddCategoria = async () => {
    if (!newCategoriaNombre.trim()) {
      toast.error('El nombre del categoria es requerido');
      return;
    }
    if (!selectedTipoGanadoObj) {
      toast.error('Primero seleccione una tipoGanado');
      return;
    }
    setAddingCategoria(true);
    try {
      const response = await axios.post(`${API}/categorias`, {
        tipoGanadoId: selectedTipoGanadoObj.id,
        nombre: newCategoriaNombre.trim()
      });
      toast.success(`Categoria "${response.data.nombre}" creado`);
      setShowAddCategoria(false);
      setNewCategoriaNombre('');
      await loadCategorias(selectedTipoGanadoObj.id);
      // Auto-select the new categoria
      onCategoriaChange(response.data.nombre);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear categoria');
    } finally {
      setAddingCategoria(false);
    }
  };

  const tipoGanadoOptions = tipoGanados.map(m => ({
    value: m.nombre,
    label: m.nombre,
  }));

  const categoriaOptions = categorias.map(m => ({
    value: m.nombre,
    label: m.nombre,
  }));

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        {/* TipoGanado Combobox */}
        <div className="space-y-2">
          <Label>Tipo de Ganado *</Label>
          <div className="flex gap-2">
            <SearchableCombobox
              value={tipoGanado}
              onSelect={handleTipoGanadoSelect}
              options={tipoGanadoOptions}
              placeholder="Seleccionar tipo de ganado..."
              emptyText="No se encontró."
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setNewTipoGanadoNombre(''); setShowAddTipoGanado(true); }}
              disabled={disabled}
              title="Agregar nuevo"
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {errors.tipoGanado && (
            <p className="text-sm text-destructive">{errors.tipoGanado}</p>
          )}
        </div>

        {/* Categoria Combobox */}
        <div className="space-y-2">
          <Label>Categoría *</Label>
          <div className="flex gap-2">
            <SearchableCombobox
              value={categoria}
              onSelect={handleCategoriaSelect}
              options={categoriaOptions}
              placeholder={tipoGanado ? "Seleccionar categoría..." : "Primero seleccione tipo"}
              emptyText="No se encontró."
              disabled={disabled || !tipoGanado}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setNewCategoriaNombre(''); setShowAddCategoria(true); }}
              disabled={disabled || !selectedTipoGanadoObj}
              title="Agregar nueva categoría"
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {errors.categoria && (
            <p className="text-sm text-destructive">{errors.categoria}</p>
          )}
        </div>
      </div>

      {/* Quick-add TipoGanado Dialog */}
      <Dialog open={showAddTipoGanado} onOpenChange={setShowAddTipoGanado}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Tipo de Ganado</DialogTitle>
            <DialogDescription>Ingrese el nombre del tipo</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newTipoGanadoNombre}
              onChange={(e) => setNewTipoGanadoNombre(e.target.value)}
              placeholder="Ej. Vacunos"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddTipoGanado()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTipoGanado(false)} disabled={addingTipoGanado}>
              Cancelar
            </Button>
            <Button onClick={handleAddTipoGanado} disabled={addingTipoGanado}>
              {addingTipoGanado ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-add Categoria Dialog */}
      <Dialog open={showAddCategoria} onOpenChange={setShowAddCategoria}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nueva Categoría</DialogTitle>
            <DialogDescription>Agregar categoría para {tipoGanado}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newCategoriaNombre}
              onChange={(e) => setNewCategoriaNombre(e.target.value)}
              placeholder="Ej. Terneros"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddCategoria()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategoria(false)} disabled={addingCategoria}>
              Cancelar
            </Button>
            <Button onClick={handleAddCategoria} disabled={addingCategoria}>
              {addingCategoria ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TipoGanadoCategoriaSelector;
